import { Button, Icons, Progress } from '@commenda/kakashi';

import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import { DataRoomFileState, type IDataRoomUploadProgress } from './types';

interface DataRoomUploadProgressProps {
  progress: IDataRoomUploadProgress | null;
  /** The room is being created or re-read, before any file row exists. */
  isPreparing: boolean;
  error: string | null;
  /** Set after a reload while an upload is unfinished: the files must be chosen again to resume it. */
  resumeFileCount: number | null;
  onFilesChoose: () => void;
  /** Null once the room has stopped on an error no retry can fix. */
  onFileRetry: ((fileId: string) => void) | null;
  onUploadCancel: () => void;
}

/** The data room above the composer input: one bar for the room ("1.2 of 4.8 GB"), one per file, and
 * "Stored, ready to read" once every file is in. */
export const DataRoomUploadProgress = ({
  progress,
  isPreparing,
  error,
  resumeFileCount,
  onFilesChoose,
  onFileRetry,
  onUploadCancel,
}: DataRoomUploadProgressProps) => {
  const visibleRows = progress
    ? DataRoomUploadHelper.visibleFileRows(progress.files)
    : [];
  const hiddenRowCount = progress
    ? progress.files.length - visibleRows.length
    : 0;

  return (
    <div className="flex w-full flex-col gap-md p-sm">
      {isPreparing && (
        <div className="flex items-center gap-md">
          <Icons.Loading01 className="size-4 shrink-0 animate-spin text-fg-secondary" />
          <p className="txt-small-medium flex-1 text-text-primary">
            Preparing the data room
          </p>
          <Button
            variant="tertiary"
            size="xs"
            type="button"
            onClick={onUploadCancel}
          >
            Cancel
          </Button>
        </div>
      )}
      {resumeFileCount !== null && !progress && (
        <div className="flex items-center gap-md">
          <Icons.UploadCloud01 className="size-4 shrink-0 text-fg-secondary" />
          <p className="txt-small-medium flex-1 text-text-primary">
            Finish uploading {resumeFileCount} files
          </p>
          <Button
            variant="secondary"
            size="xs"
            type="button"
            onClick={onFilesChoose}
          >
            Choose the files again
          </Button>
          <Button
            variant="tertiary"
            size="xs"
            type="button"
            onClick={onUploadCancel}
          >
            Cancel
          </Button>
        </div>
      )}
      {progress && (
        <div className="flex flex-col gap-sm">
          <div className="flex items-center gap-md">
            <Icons.Archive className="size-4 shrink-0 text-fg-secondary" />
            <p className="txt-small-medium flex-1 text-text-primary">
              {progress.isStored
                ? 'Stored, ready to read'
                : `Data room: ${DataRoomUploadHelper.formatProgress(progress.sentBytes, progress.totalBytes)} (${DataRoomUploadHelper.percent(progress.sentBytes, progress.totalBytes)}%)`}
            </p>
            <Button
              variant="tertiary"
              size="xs"
              type="button"
              onClick={onUploadCancel}
            >
              {progress.isStored ? 'Remove' : 'Cancel'}
            </Button>
          </div>
          <Progress
            accent={progress.isStored ? 'success' : 'blue'}
            value={DataRoomUploadHelper.percent(
              progress.sentBytes,
              progress.totalBytes
            )}
            className="rounded-full"
          />
          <ul className="flex max-h-40 flex-col gap-xs overflow-y-auto">
            {visibleRows.map((row) => (
              <li key={row.fileId} className="flex flex-col gap-xxs">
                <div className="flex items-center gap-sm">
                  <span className="txt-extra-small-regular min-w-0 flex-1 truncate text-text-secondary">
                    {row.name}
                  </span>
                  {row.state === DataRoomFileState.FAILED && onFileRetry && (
                    <Button
                      variant="link"
                      size="xs"
                      type="button"
                      onClick={() => onFileRetry(row.fileId)}
                    >
                      Upload failed, retry
                    </Button>
                  )}
                  {row.state === DataRoomFileState.FAILED && !onFileRetry && (
                    <span className="txt-extra-small-regular shrink-0 text-text-error">
                      Upload failed
                    </span>
                  )}
                  {row.state !== DataRoomFileState.FAILED && (
                    <span className="txt-extra-small-regular shrink-0 text-text-tertiary">
                      {row.state === DataRoomFileState.STORED
                        ? 'Stored'
                        : DataRoomUploadHelper.formatProgress(
                            row.sentBytes,
                            row.sizeBytes
                          )}
                    </span>
                  )}
                </div>
                <Progress
                  accent={
                    row.state === DataRoomFileState.FAILED
                      ? 'error'
                      : row.state === DataRoomFileState.STORED
                        ? 'success'
                        : 'blue'
                  }
                  value={DataRoomUploadHelper.percent(
                    row.sentBytes,
                    row.sizeBytes
                  )}
                  height={4}
                  className="h-1 rounded-full"
                />
                {row.error && (
                  <span className="txt-extra-small-regular text-text-error">
                    {row.error}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {hiddenRowCount > 0 && (
            <p className="txt-extra-small-regular text-text-tertiary">
              And {hiddenRowCount} more files
            </p>
          )}
        </div>
      )}
      {error && (
        <p className="txt-extra-small-regular text-text-error">{error}</p>
      )}
    </div>
  );
};
